import { first, isEmpty, isFunction, noop } from 'underscore';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createAsyncToken } from '@bingads-webui-universal/primitive-utilities';

// The hook handles the searchFilter by client-side filtering instead of server-side filtering.
export const makeUseOptionsAsyncDataSourceWithClientFilter = (loadOptions, filterPredicate, onDoneCallback = noop) => {
  const useOptionsAsyncDataSource = (searchFilter, optionsLimit = Number.MAX_SAFE_INTEGER) => {
    const isFirstLoad = useRef(true);
    const [isLoading, setIsLoading] = useState(true);
    const noDataAfterFirstLoad = useRef(false);
    const asyncTokenRef = useRef();

    const [isMoreOptionsThanLimit, setIsMoreOptionsThanLimit] = useState(false);
    const [srcOptions, setSrcOptions] = useState([]);
    const [options, setOptions] = useState([]);
    const [lastLoadedSearchFilter, setLastLoadedSearchFilter] = useState();
    const [loadError, setLoadError] = useState();

    const onDoneLoading = useCallback((newOptions) => {
      setIsLoading(false);
      noDataAfterFirstLoad.current = isFirstLoad.current && isEmpty(newOptions);
      isFirstLoad.current = false;
      setIsMoreOptionsThanLimit(newOptions.length > optionsLimit);
      setSrcOptions(newOptions);
      setLoadError(undefined);
      asyncTokenRef.current = null;

      if (isFunction(onDoneCallback)) {
        onDoneCallback(newOptions);
      }
    }, [optionsLimit]);
    const onErrorLoading = useCallback((error) => {
      setIsLoading(false);
      setLoadError(error);
      asyncTokenRef.current = null;
    }, []);
    const onCancelLoading = useCallback(() => {
      setIsLoading(false);
      asyncTokenRef.current = null;
    }, []);

    useEffect(() => {
      setLastLoadedSearchFilter(searchFilter);
      const filteredOptions = srcOptions.filter(filterPredicate(searchFilter));
      setIsMoreOptionsThanLimit(filteredOptions.length > optionsLimit);
      setOptions(first(filteredOptions, optionsLimit));
    }, [optionsLimit, searchFilter, srcOptions]);

    const triggerLoadOptions = useCallback(() => {
      if (asyncTokenRef.current) {
        asyncTokenRef.current.cancel();
      }
      setIsLoading(true);
      asyncTokenRef.current = createAsyncToken(loadOptions({
        top: optionsLimit + 1,
      })).on({
        onSuccess: onDoneLoading,
        onError: onErrorLoading,
        onCancel: onCancelLoading,
      });
    }, [optionsLimit, onDoneLoading, onErrorLoading, onCancelLoading]);

    useEffect(() => {
      triggerLoadOptions();

      return () => {
        if (asyncTokenRef.current) {
          asyncTokenRef.current.cancel();
        }
      };
    }, [optionsLimit, onDoneLoading, onErrorLoading, onCancelLoading, triggerLoadOptions]);

    return [isLoading, isFirstLoad.current, noDataAfterFirstLoad.current, isMoreOptionsThanLimit, options, loadError, lastLoadedSearchFilter, triggerLoadOptions];
  };

  return useOptionsAsyncDataSource;
};
